<template>
  <div class="edd-review-page">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.compliance.edd_review.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.compliance.edd_review.subtitle') }}</p>
      </div>
    </div>

    <!-- Split Panel Layout -->
    <div class="split-panel">
      <!-- Left Panel: Player List -->
      <div class="left-panel">
        <!-- Search Filter -->
        <div class="panel-search">
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="$t('admin.compliance.edd_review.search_placeholder')"
            @input="debouncedFetch"
          />
        </div>

        <!-- Loading -->
        <div v-if="loading" class="panel-loading">
          <span class="spinner"></span>
          {{ $t('admin.compliance.edd_review.loading') }}
        </div>

        <!-- Empty State -->
        <div v-else-if="players.length === 0" class="panel-empty">
          {{ $t('admin.compliance.edd_review.empty') }}
        </div>

        <!-- Player List -->
        <div v-else class="player-list">
          <div
            v-for="player in players"
            :key="player.user_id"
            class="player-card"
            :class="{ active: selectedUserId === player.user_id }"
            @click="selectPlayer(player)"
          >
            <div class="player-card-header">
              <div class="player-avatar">{{ getInitials(player.username) }}</div>
              <div class="player-card-info">
                <div class="player-card-name">{{ player.username }}</div>
                <div class="player-card-email">{{ player.email }}</div>
              </div>
              <span class="edd-status-badge" :class="eddStatusClass(player.edd_status)">
                {{ player.edd_status ?? 'pending' }}
              </span>
            </div>
            <div class="player-card-meta">
              <div class="meta-item">
                <span class="meta-label">{{ $t('admin.compliance.edd_review.label_vip') }}</span>
                <span class="meta-value">{{ vipLevelName(player.vip_level) }}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">{{ $t('admin.compliance.edd_review.label_deposits') }}</span>
                <span class="meta-value">${{ formatAmount(player.total_deposits) }}</span>
              </div>
              <div v-if="player.pep_flagged" class="pep-badge">PEP</div>
            </div>
            <div class="player-card-trigger">
              <span class="trigger-label">{{ $t('admin.compliance.edd_review.label_trigger') }}</span>
              {{ player.trigger_reason }}
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="panel-pagination">
          <button
            class="pagination-btn"
            :disabled="currentPage <= 1"
            @click="goToPage(currentPage - 1)"
          >&larr;</button>
          <span class="pagination-info">{{ currentPage }} / {{ totalPages }}</span>
          <button
            class="pagination-btn"
            :disabled="currentPage >= totalPages"
            @click="goToPage(currentPage + 1)"
          >&rarr;</button>
        </div>
      </div>

      <!-- Right Panel: Document Review -->
      <div class="right-panel">
        <!-- No Selection -->
        <div v-if="!selectedUserId" class="empty-selection">
          <div class="empty-selection-icon">&#128203;</div>
          <div class="empty-selection-text">{{ $t('admin.compliance.edd_review.panel_empty') }}</div>
        </div>

        <!-- Player Detail -->
        <template v-else>
          <!-- Player Info Header -->
          <div class="detail-header">
            <div class="detail-player-info">
              <div class="detail-avatar">{{ getInitials(selectedPlayer?.username ?? '') }}</div>
              <div>
                <div class="detail-name">{{ selectedPlayer?.username }}</div>
                <div class="detail-email">{{ selectedPlayer?.email }}</div>
              </div>
            </div>
            <div class="detail-badges">
              <span class="edd-status-badge" :class="eddStatusClass(selectedPlayer?.edd_status ?? null)">
                EDD: {{ selectedPlayer?.edd_status ?? 'pending' }}
              </span>
              <span v-if="selectedPlayer?.pep_flagged" class="pep-badge pep-badge-lg">PEP</span>
            </div>
          </div>

          <!-- Player Meta -->
          <div class="detail-meta-grid">
            <div class="detail-meta-item">
              <div class="detail-meta-label">{{ $t('admin.compliance.edd_review.meta_vip_level') }}</div>
              <div class="detail-meta-value">{{ vipLevelName(selectedPlayer?.vip_level ?? 0) }}</div>
            </div>
            <div class="detail-meta-item">
              <div class="detail-meta-label">{{ $t('admin.compliance.edd_review.meta_total_deposits') }}</div>
              <div class="detail-meta-value">${{ formatAmount(selectedPlayer?.total_deposits ?? 0) }}</div>
            </div>
            <div class="detail-meta-item">
              <div class="detail-meta-label">{{ $t('admin.compliance.edd_review.meta_trigger_reason') }}</div>
              <div class="detail-meta-value">{{ selectedPlayer?.trigger_reason }}</div>
            </div>
            <div class="detail-meta-item">
              <div class="detail-meta-label">{{ $t('admin.compliance.edd_review.meta_flagged') }}</div>
              <div class="detail-meta-value">{{ formatDate(selectedPlayer?.flagged_at ?? '') }}</div>
            </div>
          </div>

          <!-- Documents Section -->
          <div class="documents-section">
            <div class="section-title">{{ $t('admin.compliance.edd_review.section_documents') }}</div>

            <div v-if="docsLoading" class="docs-loading">
              <span class="spinner"></span>
              {{ $t('admin.compliance.edd_review.loading_docs') }}
            </div>

            <div v-else-if="documents.length === 0" class="docs-empty">
              {{ $t('admin.compliance.edd_review.no_docs') }}
            </div>

            <div v-else class="documents-list">
              <div v-for="doc in documents" :key="doc.id" class="document-card">
                <div class="document-icon">&#128196;</div>
                <div class="document-info">
                  <div class="document-name">{{ doc.original_name }}</div>
                  <div class="document-meta">
                    <span class="document-type">{{ formatDocType(doc.document_type) }}</span>
                    <span class="document-size">{{ formatFileSize(doc.file_size) }}</span>
                  </div>
                  <div v-if="doc.rejection_reason" class="document-rejection">
                    {{ $t('admin.compliance.edd_review.rejection_label') }} {{ doc.rejection_reason }}
                  </div>
                </div>
                <span class="doc-status-badge" :class="docStatusClass(doc.status)">
                  {{ doc.status }}
                </span>
              </div>
            </div>
          </div>

          <!-- Action Buttons (only show if status is pending or under_review) -->
          <div
            v-if="selectedPlayer?.edd_status === 'pending' || selectedPlayer?.edd_status === 'under_review' || selectedPlayer?.edd_status === null"
            class="action-section"
          >
            <div class="section-title">{{ $t('admin.compliance.edd_review.section_decision') }}</div>
            <div class="action-buttons">
              <button
                class="btn btn-approve"
                :disabled="actionProcessing"
                @click="openApproveConfirm"
              >
                <span v-if="actionProcessing && pendingAction === 'approve'" class="spinner spinner-sm"></span>
                &#10003; {{ $t('admin.compliance.edd_review.btn_approve') }}
              </button>
              <button
                class="btn btn-reject"
                :disabled="actionProcessing"
                @click="openRejectModal"
              >
                &#10007; {{ $t('admin.compliance.edd_review.btn_reject') }}
              </button>
            </div>
          </div>

          <!-- Resolved State -->
          <div v-else class="resolved-section">
            <div class="resolved-message" :class="resolvedClass(selectedPlayer?.edd_status ?? '')">
              <span v-if="selectedPlayer?.edd_status === 'approved'">&#10003; {{ $t('admin.compliance.edd_review.resolved_approved') }}</span>
              <span v-else-if="selectedPlayer?.edd_status === 'rejected'">&#10007; {{ $t('admin.compliance.edd_review.resolved_rejected') }}</span>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Approve Confirm Modal -->
    <div v-if="approveModal.visible" class="modal-overlay" @click.self="closeApproveModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">{{ $t('admin.compliance.edd_review.approve_modal_title') }}</h3>
          <button class="modal-close" @click="closeApproveModal">&times;</button>
        </div>
        <div class="modal-body">
          <p class="modal-text">{{ $t('admin.compliance.edd_review.approve_modal_text') }}</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" :disabled="actionProcessing" @click="closeApproveModal">{{ $t('admin.compliance.edd_review.btn_cancel') }}</button>
          <button class="btn btn-approve" :disabled="actionProcessing" @click="submitApprove">
            <span v-if="actionProcessing" class="spinner spinner-sm"></span>
            {{ $t('admin.compliance.edd_review.btn_confirm') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Reject Modal -->
    <div v-if="rejectModal.visible" class="modal-overlay" @click.self="closeRejectModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">{{ $t('admin.compliance.edd_review.reject_modal_title') }}</h3>
          <button class="modal-close" @click="closeRejectModal">&times;</button>
        </div>
        <div class="modal-body">
          <p class="modal-text">{{ $t('admin.compliance.edd_review.reject_modal_text') }}</p>
          <div class="form-group">
            <label class="form-label">Rejection Reason <span class="required">*</span></label>
            <textarea
              v-model="rejectModal.reason"
              class="form-textarea"
              placeholder="Explain why EDD is being rejected and what the player needs to provide..."
              rows="4"
              maxlength="500"
            ></textarea>
            <div class="char-count">{{ rejectModal.reason.length }}/500</div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" :disabled="actionProcessing" @click="closeRejectModal">{{ $t('admin.compliance.edd_review.btn_cancel') }}</button>
          <button
            class="btn btn-reject"
            :disabled="actionProcessing || !rejectModal.reason.trim()"
            @click="submitReject"
          >
            <span v-if="actionProcessing" class="spinner spinner-sm"></span>
            {{ $t('admin.compliance.edd_review.btn_confirm') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

const { t } = useI18n()
const toast = useToastStore()

// ----------- Types -----------

interface EddPlayer {
  user_id: number
  username: string
  email: string
  vip_level: number
  edd_status: string | null
  pep_flagged: boolean
  total_deposits: number
  trigger_reason: string
  flagged_at: string | null
}

interface EddDocument {
  id: number
  document_type: string
  original_name: string
  mime_type: string
  file_size: number
  status: string
  rejection_reason: string | null
  notes: string | null
  uploaded_at: string | null
  reviewed_at: string | null
}

interface PaginatedPlayersResponse {
  data: EddPlayer[]
  current_page: number
  last_page: number
  per_page: number
  total: number
}

interface DocumentsResponse {
  user_id: number
  username: string
  edd_status: string | null
  documents: EddDocument[]
}

// ----------- State -----------

const players = ref<EddPlayer[]>([])
const loading = ref(true)
const currentPage = ref(1)
const totalPages = ref(0)
const searchQuery = ref('')

const selectedUserId = ref<number | null>(null)
const selectedPlayer = computed(() => players.value.find((p) => p.user_id === selectedUserId.value) ?? null)

const documents = ref<EddDocument[]>([])
const docsLoading = ref(false)

const actionProcessing = ref(false)
const pendingAction = ref<'approve' | 'reject' | null>(null)

const approveModal = reactive({ visible: false })
const rejectModal = reactive({ visible: false, reason: '' })

// ----------- Data Fetching -----------

let debounceTimer: ReturnType<typeof setTimeout> | null = null

function debouncedFetch() {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    currentPage.value = 1
    fetchPlayers()
  }, 300)
}

async function fetchPlayers(): Promise<void> {
  loading.value = true
  try {
    const params: Record<string, string | number> = { page: currentPage.value }
    if (searchQuery.value) params.search = searchQuery.value

    const response = await apiClient.get<PaginatedPlayersResponse>('/admin/api/edd/pending', { params })
    players.value = response.data.data
    currentPage.value = response.data.current_page
    totalPages.value = response.data.last_page
  } catch {
    toast.error(t('admin.compliance.edd_review.error_load'))
  } finally {
    loading.value = false
  }
}

async function fetchDocuments(userId: number): Promise<void> {
  docsLoading.value = true
  documents.value = []
  try {
    const response = await apiClient.get<DocumentsResponse>(`/admin/api/edd/${userId}/documents`)
    documents.value = response.data.documents
  } catch {
    toast.error('Failed to load documents')
  } finally {
    docsLoading.value = false
  }
}

// ----------- Player Selection -----------

function selectPlayer(player: EddPlayer) {
  selectedUserId.value = player.user_id
  fetchDocuments(player.user_id)
}

// ----------- Approve -----------

function openApproveConfirm() {
  approveModal.visible = true
}

function closeApproveModal() {
  if (actionProcessing.value) return
  approveModal.visible = false
}

async function submitApprove(): Promise<void> {
  if (!selectedUserId.value) return
  actionProcessing.value = true
  pendingAction.value = 'approve'
  try {
    await apiClient.post(`/admin/api/edd/${selectedUserId.value}/approve`)
    toast.success(t('admin.compliance.edd_review.success_approve'))
    closeApproveModal()
    // Update player status in list
    const player = players.value.find((p) => p.user_id === selectedUserId.value)
    if (player) player.edd_status = 'approved'
    await fetchDocuments(selectedUserId.value)
  } catch {
    toast.error(t('admin.compliance.edd_review.error_approve'))
  } finally {
    actionProcessing.value = false
    pendingAction.value = null
  }
}

// ----------- Reject -----------

function openRejectModal() {
  rejectModal.visible = true
  rejectModal.reason = ''
}

function closeRejectModal() {
  if (actionProcessing.value) return
  rejectModal.visible = false
}

async function submitReject(): Promise<void> {
  if (!selectedUserId.value || !rejectModal.reason.trim()) return
  actionProcessing.value = true
  pendingAction.value = 'reject'
  try {
    await apiClient.post(`/admin/api/edd/${selectedUserId.value}/reject`, {
      reason: rejectModal.reason.trim(),
    })
    toast.success(t('admin.compliance.edd_review.success_reject'))
    closeRejectModal()
    // Update player status in list
    const player = players.value.find((p) => p.user_id === selectedUserId.value)
    if (player) player.edd_status = 'rejected'
    await fetchDocuments(selectedUserId.value)
  } catch {
    toast.error(t('admin.compliance.edd_review.error_reject'))
  } finally {
    actionProcessing.value = false
    pendingAction.value = null
  }
}

// ----------- Pagination -----------

function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  fetchPlayers()
}

// ----------- Formatting -----------

const VIP_NAMES: Record<number, string> = {
  0: 'Standard',
  1: 'Bronze',
  2: 'Silver',
  3: 'Gold',
  4: 'Platinum',
  5: 'Diamond',
}

function vipLevelName(level: number): string {
  return VIP_NAMES[level] ?? `Level ${level}`
}

function formatAmount(amount: number): string {
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(iso: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function getInitials(username: string): string {
  if (!username) return '?'
  return username
    .split(/[_.\-\s]/)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function formatDocType(docType: string): string {
  const map: Record<string, string> = {
    bank_statement: 'Bank Statement',
    tax_return: 'Tax Return',
    employment_contract: 'Employment Contract',
    investment_portfolio: 'Investment Portfolio',
    inheritance_proof: 'Inheritance Proof',
    other: 'Other',
  }
  return map[docType] ?? docType
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function eddStatusClass(status: string | null): string {
  switch (status) {
    case 'pending': return 'status-pending'
    case 'under_review': return 'status-review'
    case 'approved': return 'status-approved'
    case 'rejected': return 'status-rejected'
    default: return 'status-pending'
  }
}

function docStatusClass(status: string): string {
  switch (status) {
    case 'approved': return 'doc-approved'
    case 'rejected': return 'doc-rejected'
    default: return 'doc-pending'
  }
}

function resolvedClass(status: string): string {
  return status === 'approved' ? 'resolved-approved' : 'resolved-rejected'
}

// ----------- Lifecycle -----------

onMounted(() => {
  fetchPlayers()
})
</script>

<style scoped>
.edd-review-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
  display: flex;
  flex-direction: column;
  height: 100%;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  margin-bottom: 20px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

/* Split Panel */
.split-panel {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 20px;
  flex: 1;
  min-height: 0;
}

/* Left Panel */
.left-panel {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-search {
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.search-input {
  width: 100%;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  box-sizing: border-box;
  transition: border-color 150ms ease;
}

.search-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.search-input::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

.panel-loading,
.panel-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 16px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 13px;
}

.player-list {
  overflow-y: auto;
  flex: 1;
}

/* Player Card */
.player-card {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  cursor: pointer;
  transition: background 150ms ease;
}

.player-card:hover {
  background: #213743;
}

.player-card.active {
  background: rgba(20, 117, 225, 0.12);
  border-left: 3px solid var(--accent-secondary, #1475e1);
}

.player-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.player-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1475e1, #9b59b6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.player-card-info {
  flex: 1;
  min-width: 0;
}

.player-card-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.player-card-email {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.player-card-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.meta-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
}

.meta-value {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
}

.player-card-trigger {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.trigger-label {
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  margin-right: 2px;
}

/* Panel Pagination */
.panel-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-btn {
  width: 28px;
  height: 28px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  cursor: pointer;
  transition: all 150ms ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
}

.pagination-btn:hover:not(:disabled) {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Right Panel */
.right-panel {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  overflow-y: auto;
  padding: 24px;
}

.empty-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 12px;
}

.empty-selection-icon {
  font-size: 48px;
  opacity: 0.4;
}

.empty-selection-text {
  font-size: 14px;
  color: var(--text-tertiary, #7f8fa4);
  text-align: center;
}

/* Detail Header */
.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.detail-player-info {
  display: flex;
  align-items: center;
  gap: 14px;
}

.detail-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1475e1, #9b59b6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
}

.detail-name {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.detail-email {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
}

.detail-badges {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Detail Meta Grid */
.detail-meta-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px;
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
}

.detail-meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-meta-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
}

.detail-meta-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

/* Documents Section */
.documents-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.docs-loading,
.docs-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 13px;
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.document-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.document-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.document-info {
  flex: 1;
  min-width: 0;
}

.document-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.document-meta {
  display: flex;
  gap: 12px;
  margin-top: 2px;
}

.document-type {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.document-size {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.document-rejection {
  font-size: 11px;
  color: var(--color-danger, #ed4245);
  margin-top: 4px;
}

/* Action Section */
.action-section {
  margin-bottom: 20px;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

/* Resolved Section */
.resolved-section {
  margin-bottom: 20px;
}

.resolved-message {
  padding: 16px 20px;
  border-radius: var(--radius-md, 8px);
  font-size: 14px;
  font-weight: 600;
}

.resolved-approved {
  background: rgba(0, 231, 1, 0.1);
  color: var(--color-success, #00e701);
  border: 1px solid rgba(0, 231, 1, 0.2);
}

.resolved-rejected {
  background: rgba(237, 66, 69, 0.1);
  color: var(--color-danger, #ed4245);
  border: 1px solid rgba(237, 66, 69, 0.2);
}

/* EDD Status Badges */
.edd-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 9px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.status-pending {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.status-review {
  background: rgba(20, 117, 225, 0.15);
  color: var(--accent-secondary, #1475e1);
}

.status-approved {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.status-rejected {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

/* PEP Badge */
.pep-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 7px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  background: rgba(237, 66, 69, 0.2);
  color: var(--color-danger, #ed4245);
  border: 1px solid rgba(237, 66, 69, 0.3);
}

.pep-badge-lg {
  padding: 4px 10px;
  font-size: 11px;
}

/* Document Status Badges */
.doc-status-badge {
  padding: 3px 9px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

.doc-pending {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.doc-approved {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.doc-rejected {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover:not(:disabled) {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

.btn-approve {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
  border: 1px solid rgba(0, 231, 1, 0.3);
}

.btn-approve:hover:not(:disabled) {
  background: rgba(0, 231, 1, 0.25);
}

.btn-reject {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
  border: 1px solid rgba(237, 66, 69, 0.3);
}

.btn-reject:hover:not(:disabled) {
  background: rgba(237, 66, 69, 0.25);
}

/* Loading Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  flex-shrink: 0;
}

.spinner-sm {
  width: 14px;
  height: 14px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.15s ease;
}

.modal-content {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg, 12px);
  width: 100%;
  max-width: 480px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
  font-family: inherit;
}

.modal-close:hover {
  color: var(--text-primary, #fff);
}

.modal-body {
  padding: 20px 24px;
}

.modal-text {
  font-size: 14px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.5;
  margin: 0 0 16px;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* Form */
.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 6px;
}

.required {
  color: var(--color-danger, #ed4245);
}

.form-textarea {
  width: 100%;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 150ms ease;
}

.form-textarea:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.form-textarea::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

.char-count {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  text-align: right;
  margin-top: 4px;
}

/* Responsive */
@media (max-width: 1100px) {
  .split-panel {
    grid-template-columns: 300px 1fr;
  }

  .detail-meta-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .edd-review-page {
    padding: 16px;
  }

  .split-panel {
    grid-template-columns: 1fr;
  }

  .left-panel {
    max-height: 300px;
  }
}
</style>
